import companyLogo from "@assets/images/S&P_Global_logo.svg"
import AccountCircleIcon from "@mui/icons-material/AccountCircle"
import { NavLink, useSearchParams } from "react-router"
import { Box, Menu, MenuItem } from "@mui/material"
import {
  AccessCode,
  accessStatusAtom,
  hasAccess,
  isLoggedIn,
  loggedInUserAtom,
  logout,
  permissionsAtom,
} from "@store/authAtoms"
import { getDefaultStore, useAtomValue } from "jotai"
import { ProtectedOutlet } from "@components/ProtectedOutlet"
import React, { useMemo, useState } from "react"
import { store } from "@store/store"
import { accessApi } from "@store/services/accessService"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp"
import LogoutDialog from "./LogoutDialog"
import configuration from "@utils/configuration"
import MyPermissionDialog from "./MyPermissionDialog"
import { authRedirectTargetUrl, checkSetPassOnParams } from "@store/services/authQuery"
import { GraphBuilderPagePath } from "@features/builder/const"
import { ShortcutsDialog, ShortcutsMenuItem } from "@features/home/ShortcutsDialog"
import { VersionDialog } from "@features/home/VersionDialog"

const LOCAL_ENV_COLOR = "#61de72"
const isLocalFe = process.env.NODE_ENV === "development"
const isLocalBe = import.meta.env.VITE_APP_API_URL?.includes("localhost")

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- ANY :(
export const permissionLoader = async ({ request }: { request: any }) => {
  if (!isLoggedIn() && !checkSetPassOnParams()) {
    authRedirectTargetUrl()
  }

  const jotaiStore = getDefaultStore()
  const cachedPermissions = jotaiStore.get(permissionsAtom)

  if (cachedPermissions) {
    jotaiStore.set(accessStatusAtom, {
      code: hasAccess(cachedPermissions, request.url) ? AccessCode.OK : AccessCode.FORBIDDEN,
      url: new URL(request.url).pathname,
    })
    return cachedPermissions
  }

  const promise = store.dispatch(accessApi.endpoints?.getPermissions.initiate())
  const { data: permissions, isSuccess } = await promise
  promise.unsubscribe()

  if (!isSuccess) {
    throw new Response("Failed to load permissions", { status: 503 })
  }

  jotaiStore.set(permissionsAtom, permissions)

  // Trying to access application using url without access permission to this url
  if (!hasAccess(permissions, request.url)) {
    jotaiStore.set(accessStatusAtom, {
      code: AccessCode.FORBIDDEN,
      url: new URL(request.url).pathname,
    })
  } else {
    jotaiStore.set(accessStatusAtom, {
      code: AccessCode.OK,
      url: new URL(request.url).pathname,
    })
  }
  return permissions
}

const navigateWithGraphFamilyKey = (params: URLSearchParams, to: string): string => {
  let graphKey = params.get("graphKey")
  let familyKey = params.get("familyKey")
  if (!graphKey && !familyKey) return to

  // try to figure out familyKey / graphKey one from another if provided
  if (!graphKey && familyKey) graphKey = familyKey + `_0.0.${Number.MAX_SAFE_INTEGER}` // we dont know the exact version
  if (!familyKey && graphKey) familyKey = graphKey.substring(0, graphKey.lastIndexOf("_"))

  switch (to) {
    case "/Results": {
      return `${to}?familyKey=${encodeURIComponent(familyKey ?? "")}`
    }
    case "/Build":
    case "/ScenarioDesign": {
      return `${to}?graphKey=${encodeURIComponent(graphKey ?? "")}`
    }
    default: {
      return to
    }
  }
}

function Home() {
  const user = useAtomValue(loggedInUserAtom)
  const permissions = useAtomValue(permissionsAtom)

  const [searchParams] = useSearchParams()

  const app_name = import.meta.env.VITE_PRODUCT_NAME

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [isLogoutOpen, setIsLogoutOpen] = useState(false)
  const [isPermissionOpen, setIsPermissionOpen] = useState(false)

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  const menuOptions = useMemo(
    () => [
      {
        name: "Data",
        to: "/Data",
        display: hasAccess(permissions, "/Data") ? "" : "none",
        disabled: true,
      },
      {
        name: "Build",
        to: GraphBuilderPagePath,
        display: hasAccess(permissions, GraphBuilderPagePath) ? "" : "none",
      },
      {
        name: "Calibrate",
        to: "/Calibrate",
        display: hasAccess(permissions, "/Calibrate") ? "" : "none",
        disabled: true,
      },
      {
        name: "Scenario Design",
        to: "/ScenarioDesign",
        display: hasAccess(permissions, "/ScenarioDesign") ? "" : "none",
      },
      {
        name: "Results",
        to: "/Results",
        display: hasAccess(permissions, "/Results") ? "" : "none",
      },
      {
        name: "Administration",
        to: "/Administration",
        display: hasAccess(permissions, "/Administration") ? "" : "none",
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
    [permissions, hasAccess],
  )

  return (
    <Box sx={{ height: "100%" }}>
      <Box className="app-container">
        <Box className="nav">
          <div className="flex">
            <NavLink className="nava" to={"/"}>
              <span className="title">
                <img
                  src={companyLogo}
                  alt="S&P Global"
                  style={{ backgroundColor: isLocalFe ? LOCAL_ENV_COLOR : undefined }}
                />
                <h2
                  className="title"
                  style={{ backgroundColor: isLocalBe ? LOCAL_ENV_COLOR : undefined }}
                >
                  {app_name}
                </h2>
              </span>
            </NavLink>
          </div>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {menuOptions.map((option) =>
              !option.disabled ? (
                <NavLink
                  key={option.name}
                  style={({ isActive }) => ({
                    color: isActive ? "#d6002a" : "#000",
                    display: option.display,
                    disabled: option.disabled,
                    padding: "10px",
                    fontWeight: "bold",
                    textDecoration: "none",
                  })}
                  className="hover-underline-animation"
                  to={navigateWithGraphFamilyKey(searchParams, option.to)}
                >
                  {option.name}
                </NavLink>
              ) : (
                <div key={option.name} style={{ color: "gray", padding: "10px" }}>
                  {option.name}
                </div>
              ),
            )}
            <a className="btn logoutButton" onClick={handleClick}>
              <AccountCircleIcon
                sx={{
                  verticalAlign: "middle",
                  position: "relative",
                  bottom: "2px",
                  ml: "4px",
                  mr: "2px",
                }}
              />
              <span>{user?.name}</span>
              {anchorEl ? (
                <KeyboardArrowUpIcon
                  sx={{
                    verticalAlign: "middle",
                    position: "relative",
                    bottom: "2px",
                  }}
                />
              ) : (
                <KeyboardArrowDownIcon
                  sx={{
                    verticalAlign: "middle",
                    position: "relative",
                    bottom: "2px",
                  }}
                />
              )}
            </a>
            <Menu
              id="menu"
              anchorEl={anchorEl}
              open={!!anchorEl}
              onClose={handleClose}
              MenuListProps={{
                "aria-labelledby": "button",
                sx: { minWidth: "200px", width: anchorEl && anchorEl.offsetWidth },
              }}
            >
              <MenuItem>
                <b>{user?.name}</b>
              </MenuItem>
              {configuration.MY_PERMISSIONS_VISIBLE && (
                <MenuItem onClick={() => setIsPermissionOpen(true)}>View Permissions</MenuItem>
              )}
              <ShortcutsMenuItem />
              <MenuItem onClick={() => setIsLogoutOpen(true)}>Logout</MenuItem>
              <Box sx={{ px: 2, py: 1 }}>
                <VersionDialog />
              </Box>
            </Menu>
          </Box>
          <LogoutDialog
            open={isLogoutOpen}
            setOpen={setIsLogoutOpen}
            onAcceptClick={() => {
              logout()
            }}
          />
          <MyPermissionDialog
            permissions={permissions || {}}
            open={isPermissionOpen}
            setOpen={setIsPermissionOpen}
          />
          <ShortcutsDialog />
        </Box>
        <ProtectedOutlet />
      </Box>
    </Box>
  )
}

export default Home
